/**
 * Collect scenes that appear before a target in narrative order (v2 flat tree).
 */

import type { Scenario, Scene } from '../types';
import { getContentItemLabel, getContentItemSummary } from '../components/scenario-editor/utils';
import { buildChapterGroups } from './inline-scene-model';

export function sceneTreeContainsTarget(scenes: Scene[], targetId: string): boolean {
  for (const scene of scenes) {
    if (scene.id === targetId) return true;
    if (scene.type === 'choice') {
      for (const option of scene.options) {
        if (option.scenes?.length && sceneTreeContainsTarget(option.scenes, targetId)) {
          return true;
        }
      }
    }
  }
  return false;
}

/** Scenes visited in reading order before `targetId` (target excluded). */
export function collectScenesBeforeTarget(rootScenes: Scene[], targetId: string): Scene[] {
  const result: Scene[] = [];

  function walk(list: Scene[]): boolean {
    for (const scene of list) {
      if (scene.id === targetId) return true;

      result.push(scene);

      if (scene.type === 'choice') {
        for (const option of scene.options) {
          if (option.scenes?.length && sceneTreeContainsTarget(option.scenes, targetId)) {
            if (walk(option.scenes)) return true;
            break;
          }
        }
      }
    }
    return false;
  }

  walk(rootScenes);
  return result;
}

export function getSceneSelectLabel(scene: Scene): string {
  const typeLabel =
    scene.type === 'dialogue' && scene.isInternal
      ? 'Monologue'
      : getContentItemLabel(scene);
  const summary = getContentItemSummary(scene);
  return summary ? `[${typeLabel}] "${summary}"` : `[${typeLabel}]`;
}

/** Root-level scenes of the chapter that contains `targetSceneId`. */
export function findChapterRootScenes(scenario: Scenario, targetSceneId: string): Scene[] {
  const groups = buildChapterGroups(scenario);
  for (const group of groups) {
    if (sceneTreeContainsTarget(group.scenes, targetSceneId)) {
      return group.scenes;
    }
  }
  return scenario.scenes;
}

export function listPriorSceneSelectOptions(
  scenario: Scenario,
  targetSceneId: string
): Array<{ id: string; label: string }> {
  const chapterScenes = findChapterRootScenes(scenario, targetSceneId);
  return collectScenesBeforeTarget(chapterScenes, targetSceneId).map((scene) => ({
    id: scene.id,
    label: getSceneSelectLabel(scene),
  }));
}
